import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';

// models
import {
  ExternalEntityAssociationCreateCommand,
  ExternalEntityAssociationUpdateCommand
} from '../../../models/Entities/Person/ExternalAssociation/ExternalAssociationModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

// functions
import * as ownFunctions from './PersonExternalAssociation.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const PersonExternalAssociationFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  const [externalEntityOrganization, setExternalEntityOrganization] = useState<string>('');
  const [externalEntityId, setExternalEntityId] = useState<string>('');
  const [externalEntityType, setExternalEntityType] = useState<string>('');

  useEffect(() => {
    if (props.externalAssociation) {
      setExternalEntityOrganization(props.externalAssociation.externalEntityOrganization || '');
      setExternalEntityId(props.externalAssociation.externalEntityId || '');
      setExternalEntityType(props.externalAssociation.externalEntityType || '');
    } else {
      resetForm();
    }
  }, [props.externalAssociation, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setExternalEntityOrganization('');
    setExternalEntityId('');
    setExternalEntityType('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!externalEntityOrganization.trim()) {
      errors.push(
        t('persons.externalAssociations.form.validation.organization', {
          defaultValue: 'La organización es obligatoria'
        })
      );
    }

    if (!externalEntityId.trim()) {
      errors.push(
        t('persons.externalAssociations.form.validation.externalId', {
          defaultValue: 'El identificador externo es obligatorio'
        })
      );
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: ExternalEntityAssociationCreateCommand = {
      personId: props.PersonId,
      externalEntityOrganization: externalEntityOrganization.trim(),
      externalEntityId: externalEntityId.trim(),
      externalEntityType: externalEntityType.trim() || null
    };

    if (props.externalAssociation) {
      props.UpdateExternalAssociation({
        ...payload,
        id: props.externalAssociation.id
      } as ExternalEntityAssociationUpdateCommand);
    } else {
      props.CreateExternalAssociation(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  return (
    <Drawer
      open={props.isOpen}
      type="overlay"
      position="end"
      size="small"
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          {props.externalAssociation
            ? t('persons.externalAssociations.form.editTitle', { defaultValue: 'Editar asociación externa' })
            : t('persons.externalAssociations.form.addTitle', { defaultValue: 'Nueva asociación externa' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error" layout="multiline" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>
                {t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label={t('persons.externalAssociations.form.organization', { defaultValue: 'Organización externa' })}
            required
            validationMessage={
              submitted && !externalEntityOrganization.trim()
                ? t('persons.externalAssociations.form.validation.organization', {
                    defaultValue: 'La organización es obligatoria'
                  })
                : undefined
            }
            validationState={submitted && !externalEntityOrganization.trim() ? 'error' : undefined}
          >
            <Input
              value={externalEntityOrganization}
              onChange={(_, data) => setExternalEntityOrganization(data.value || '')}
            />
          </Field>

          <Field
            label={t('persons.externalAssociations.form.externalId', { defaultValue: 'Identificador externo' })}
            required
            validationMessage={
              submitted && !externalEntityId.trim()
                ? t('persons.externalAssociations.form.validation.externalId', {
                    defaultValue: 'El identificador externo es obligatorio'
                  })
                : undefined
            }
            validationState={submitted && !externalEntityId.trim() ? 'error' : undefined}
          >
            <Input value={externalEntityId} onChange={(_, data) => setExternalEntityId(data.value || '')} />
          </Field>

          <Field label={t('persons.externalAssociations.form.externalType', { defaultValue: 'Tipo de entidad' })}>
            <Input value={externalEntityType} onChange={(_, data) => setExternalEntityType(data.value || '')} />
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', width: '100%' }}>
          <Button appearance="secondary" onClick={handleCancel}>
            <Trans i18nKey={'persons.form.footer.cancel'} defaultValue="Cancelar" />
          </Button>
          <Button appearance="primary" onClick={handleSave} disabled={props.isAdding || props.isUpdating}>
            <Trans i18nKey={'persons.form.footer.save'} defaultValue="Guardar" />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonExternalAssociationFormComponent as any);
